import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../components/FeatherIcon'
import { cleanName, normName } from './format'
import type { BulkMediaAccount, CategoryCoverage } from './types'

interface Props {
  /** Selected Accounts; coverage counts only these. */
  accountIds: string[]
  accounts: BulkMediaAccount[]
  coverage: CategoryCoverage[] | null
  loading: boolean
  errors: Record<string, string>
  loadError?: string
  /** Names typed with New that no chosen Account has yet. */
  extra: string[]
  onExtra: (names: string[]) => void
  picked: string[]
  onPick: (names: string[]) => void
  max: number
}

/**
 * Category chips across the chosen Accounts ("Feet · 3/5"): pick the Categories the
 * files go into; Accounts missing one get it created. New adds a name nobody has yet.
 */
export function CategoryPicker({ accountIds, accounts, coverage, loading, errors, loadError, extra, onExtra, picked, onPick, max }: Props) {
  const { t } = useTranslation('bulkMedia')
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState('')
  const [q, setQ] = useState('')
  const pickedNorm = new Set(picked.map(normName))
  const total = accountIds.length

  // Typed and picked names stay as chips even when no chosen Account has them (any more).
  const known = new Set((coverage ?? []).map((c) => normName(c.name)))
  const loose = [...extra, ...picked].filter((n, i, all) => !known.has(normName(n)) && all.findIndex((m) => normName(m) === normName(n)) === i)
  const chips: { name: string; have: number }[] = [
    ...loose.map((name) => ({ name, have: 0 })),
    ...(coverage ?? []).map((c) => ({ name: c.name, have: c.account_ids.filter((id) => accountIds.includes(id)).length })),
  ]

  // Search narrows the chips; picked ones stay so the choice is always in view.
  const query = normName(q)
  const shown = query ? chips.filter((c) => pickedNorm.has(normName(c.name)) || normName(c.name).includes(query)) : chips

  const toggle = (name: string) => {
    if (pickedNorm.has(normName(name))) onPick(picked.filter((n) => normName(n) !== normName(name)))
    else if (picked.length < max) onPick([...picked, name])
  }

  const submitNew = () => {
    const name = cleanName(draft)
    setDraft('')
    setAdding(false)
    if (!name) return
    const existing = chips.find((c) => normName(c.name) === normName(name))
    if (!existing) onExtra([name, ...extra])
    if (!pickedNorm.has(normName(name)) && picked.length < max) onPick([...picked, existing?.name ?? name])
  }

  const nameOf = (id: string) => {
    const a = accounts.find((x) => x.account_id === id)
    return a ? (a.username ? `@${a.username}` : a.name) : id
  }

  if (!total) return <p className="bm-muted">{t('categories.pickAccounts')}</p>

  return (
    <div className="bm-cats">
      {chips.length > 0 && (
        <div className="bm-search">
          <FeatherIcon name="search" size={14} />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('categories.search')}
            aria-label={t('categories.search')}
          />
        </div>
      )}
      <div className="bm-chips" role="group" aria-label={t('categories.title')}>
        {adding ? (
          <form
            className="bm-newcat"
            onSubmit={(e) => {
              e.preventDefault()
              submitNew()
            }}
          >
            <input
              autoFocus
              value={draft}
              maxLength={60}
              onChange={(e) => setDraft(e.target.value)}
              onBlur={submitNew}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  setDraft('')
                  setAdding(false)
                }
              }}
              placeholder={t('categories.newPlaceholder')}
              aria-label={t('categories.newPlaceholder')}
            />
          </form>
        ) : (
          <button type="button" className="bm-chip bm-chip--new" onClick={() => setAdding(true)}>
            <FeatherIcon name="plus" size={13} />
            {t('categories.new')}
          </button>
        )}
        {shown.map((c) => {
          const on = pickedNorm.has(normName(c.name))
          return (
            <button
              key={normName(c.name)}
              type="button"
              className={`bm-chip ${on ? 'is-on' : ''}`}
              aria-pressed={on}
              onClick={() => toggle(c.name)}
              title={c.have < total ? t('categories.willCreate', { count: total - c.have }) : t('categories.everywhere')}
            >
              {on && <FeatherIcon name="check" size={13} />}
              {c.name}
              <span className={`bm-chip-n ${c.have < total ? 'is-partial' : ''}`}>
                {c.have}/{total}
              </span>
            </button>
          )
        })}
        {query && shown.length === 0 && <span className="bm-muted">{t('categories.none')}</span>}
        {loading && <span className="bm-muted bm-loading">{t('categories.reading')}</span>}
      </div>
      {loadError && <p className="bm-error">{loadError}</p>}
      {Object.entries(errors).map(([id, msg]) => (
        <p key={id} className="bm-error">
          {t('categories.readFailed', { account: nameOf(id), message: msg })}
        </p>
      ))}
      {picked.length > 0 && <p className="bm-hint">{t('categories.hint')}</p>}
      {picked.length >= max && <p className="bm-hint">{t('categories.max', { max })}</p>}
    </div>
  )
}
